/**
 * The org record's JSON columns (spec §4, §5, §6, §10), typed and validated
 * at the one boundary they cross: a Hub form or API body becoming a jsonb
 * value the app will read verbatim in phase 2.
 *
 * PURE. No database import: `@hub/ui/*` imports these validators so a form
 * refuses the same input the route refuses, and the browser bundle must not
 * drag `@hub/db` in. Validators return a `Validated<T>` rather than
 * throwing, so a route turns `ok: false` into a 400 with the message and
 * nothing else.
 */

import { BOT_IDS, FEATURE_IDS, INTEGRATION_IDS } from "@hub/lib/org-switches";
import { SLUG_RE } from "@hub/lib/slug";

export const ORG_CHANNELS = ["early", "stable"] as const;
export type OrgChannel = (typeof ORG_CHANNELS)[number];
export function isOrgChannel(value: unknown): value is OrgChannel {
  return value === "early" || value === "stable";
}

export const ORG_STATUSES = ["active", "suspended"] as const;
export type OrgStatus = (typeof ORG_STATUSES)[number];
export function isOrgStatus(value: unknown): value is OrgStatus {
  return value === "active" || value === "suspended";
}

export interface OrgTeam {
  id: string;
  label: string;
  /**
   * The team's first-run role card (phase 4). `null` means the team has no
   * card; on input, absent means the label and `""` means null (the hub
   * form's blank).
   */
  cardLabel: string | null;
  description: string;
  suggestedConnections: string[];
  landingBot: string | null;
}

export interface OrgGlossaryEntry {
  term: string;
  meaning: string;
}

export interface OrgBrand {
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string | null;
  fontFamily: string | null;
  documentFooter: string;
}

export interface OrgProfile {
  name: string;
  legalName: string | null;
  description: string;
  industry: string;
  emailDomains: string[];
  teams: OrgTeam[];
  glossary: OrgGlossaryEntry[];
  writingStyle: string;
  brand: OrgBrand;
  supportContact: string;
  assistantName: string | null;
}

export interface OrgSwitches {
  features: string[];
  integrations: string[];
  bots: string[];
}

/** The admin consent a customer's IT gave the org's Microsoft app (phase 6). */
export interface OrgConsent {
  tenantId: string;
  /** ISO 8601. */
  grantedAt: string;
}

/** The last consent attempt that did not prove a grant (phase 6). */
export interface OrgConsentError {
  code: string;
  /** ISO 8601. */
  at: string;
}

export interface OrgSignin {
  provider: "microsoft" | "google";
  app: "shared" | "own";
  clientId: string | null;
  tenantId: string | null;
  /**
   * Written only by the admin-consent callback. Optional so `NYMBL_SIGNIN`
   * and every phase-1 row keep their bytes; `validateOrgSignin` never emits
   * them (a form cannot claim a consent).
   */
  consent?: OrgConsent | null;
  consentError?: OrgConsentError | null;
}

/**
 * A Microsoft tenant's Directory (tenant) ID, lowercased. Not a domain: the
 * box compares it with the id token's `tid`, which is always the GUID.
 */
export const TENANT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** A consent failure code as recorded: Microsoft's `error` or one of ours. */
export const CONSENT_CODE_RE = /^[a-z0-9_]{1,40}$/;

export type Validated<T> = { ok: true; value: T } | { ok: false; error: string };

const ok = <T>(value: T): Validated<T> => ({ ok: true, value });
const bad = <T>(error: string): Validated<T> => ({ ok: false, error });

const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const COLOR_RE = /^#[0-9a-fA-F]{6}$/;
/** Same rule as the box's BRAND_FONT_NAME: a font name that cannot break out of CSS. */
const FONT_NAME_RE = /^[\p{L}\p{N} ._'-]{1,80}$/u;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TEAM_ID_RE = /^[a-z0-9_]{1,40}$/;
/** What `Date.prototype.toISOString()` writes. */
const ISO_TIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;

function isIsoTime(v: unknown): v is string {
  return typeof v === "string" && ISO_TIME_RE.test(v) && !Number.isNaN(Date.parse(v));
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** A trimmed string of at most `max` chars; `""` is allowed unless `min` > 0. */
function str(v: unknown, field: string, max: number, min = 0): Validated<string> {
  if (typeof v !== "string") return bad(`${field} must be a string`);
  const t = v.trim();
  if (t.length < min) return bad(`${field} is required`);
  if (t.length > max) return bad(`${field} must be at most ${max} characters`);
  return ok(t);
}

function nullableStr(v: unknown, field: string, max: number): Validated<string | null> {
  if (v === null || v === undefined || v === "") return ok(null);
  const r = str(v, field, max, 1);
  return r.ok ? ok(r.value) : bad(r.error);
}

function stringList(v: unknown, field: string): Validated<string[]> {
  if (!Array.isArray(v)) return bad(`${field} must be a list`);
  const out: string[] = [];
  for (const item of v) {
    if (typeof item !== "string") return bad(`${field} must contain only strings`);
    out.push(item.trim());
  }
  return ok(out);
}

function knownIds(v: unknown, field: string, known: readonly string[]): Validated<string[]> {
  const list = stringList(v, field);
  if (!list.ok) return list;
  for (const id of list.value) {
    if (!known.includes(id)) return bad(`${field}: "${id}" is not a known id`);
  }
  return ok([...new Set(list.value)].sort());
}

export function validateOrgSlug(input: unknown): Validated<string> {
  if (typeof input !== "string") return bad("slug must be a string");
  const slug = input.trim();
  if (slug.length === 0 || slug.length > 40 || !SLUG_RE.test(slug)) {
    return bad("slug must be a lowercase DNS label of at most 40 characters");
  }
  return ok(slug);
}

function validateTeam(v: unknown, index: number): Validated<OrgTeam> {
  if (!isRecord(v)) return bad(`teams[${index}] must be an object`);
  if (typeof v.id !== "string" || !TEAM_ID_RE.test(v.id)) {
    return bad(`teams[${index}].id must match ${TEAM_ID_RE}`);
  }
  const label = str(v.label, `teams[${index}].label`, 60, 1);
  if (!label.ok) return bad(label.error);
  const description = str(v.description ?? "", `teams[${index}].description`, 300);
  if (!description.ok) return bad(description.error);
  let cardLabel: string | null = label.value;
  if (v.cardLabel === null || v.cardLabel === "") cardLabel = null;
  else if (v.cardLabel !== undefined) {
    if (typeof v.cardLabel !== "string")
      return bad(`teams[${index}].cardLabel must be a string or null`);
    const c = str(v.cardLabel, `teams[${index}].cardLabel`, 60, 1);
    if (!c.ok) return bad(c.error);
    cardLabel = c.value;
  }
  const connections = knownIds(
    v.suggestedConnections ?? [],
    `teams[${index}].suggestedConnections`,
    INTEGRATION_IDS,
  );
  if (!connections.ok) return bad(connections.error);
  let landingBot: string | null = null;
  if (v.landingBot !== null && v.landingBot !== undefined && v.landingBot !== "") {
    if (typeof v.landingBot !== "string" || !BOT_IDS.includes(v.landingBot)) {
      return bad(`teams[${index}].landingBot is not a known bot id`);
    }
    landingBot = v.landingBot;
  }
  return ok({
    id: v.id,
    label: label.value,
    cardLabel,
    description: description.value,
    suggestedConnections: connections.value,
    landingBot,
  });
}

function validateBrand(v: unknown): Validated<OrgBrand> {
  if (!isRecord(v)) return bad("brand must be an object");
  for (const field of ["primaryColor", "secondaryColor"] as const) {
    if (typeof v[field] !== "string" || !COLOR_RE.test(v[field] as string)) {
      return bad(`brand.${field} must be a #rrggbb colour`);
    }
  }
  let logoUrl: string | null = null;
  if (v.logoUrl !== null && v.logoUrl !== undefined && v.logoUrl !== "") {
    if (typeof v.logoUrl !== "string") return bad("brand.logoUrl must be a string");
    try {
      if (new URL(v.logoUrl).protocol !== "https:") return bad("brand.logoUrl must be https");
    } catch {
      return bad("brand.logoUrl must be an absolute https URL");
    }
    logoUrl = v.logoUrl;
  }
  const fontFamily = nullableStr(v.fontFamily, "brand.fontFamily", 80);
  if (!fontFamily.ok) return bad(fontFamily.error);
  if (fontFamily.value !== null && !FONT_NAME_RE.test(fontFamily.value)) {
    return bad("brand.fontFamily may use letters, digits, spaces and . _ ' - only");
  }
  const footer = str(v.documentFooter ?? "", "brand.documentFooter", 200);
  if (!footer.ok) return bad(footer.error);
  return ok({
    primaryColor: (v.primaryColor as string).toUpperCase(),
    secondaryColor: (v.secondaryColor as string).toUpperCase(),
    logoUrl,
    fontFamily: fontFamily.value,
    documentFooter: footer.value,
  });
}

export function validateOrgProfile(input: unknown): Validated<OrgProfile> {
  if (!isRecord(input)) return bad("profile must be an object");
  const name = str(input.name, "name", 120, 1);
  if (!name.ok) return bad(name.error);
  const legalName = nullableStr(input.legalName, "legalName", 200);
  if (!legalName.ok) return bad(legalName.error);
  const description = str(input.description ?? "", "description", 2000);
  if (!description.ok) return bad(description.error);
  const industry = str(input.industry ?? "", "industry", 120);
  if (!industry.ok) return bad(industry.error);

  const domains = stringList(input.emailDomains, "emailDomains");
  if (!domains.ok) return bad(domains.error);
  const emailDomains = [...new Set(domains.value.map((d) => d.toLowerCase()))];
  if (emailDomains.length === 0) return bad("at least one email domain is required");
  for (const d of emailDomains) {
    if (!DOMAIN_RE.test(d)) return bad(`"${d}" is not a domain`);
  }

  if (!Array.isArray(input.teams)) return bad("teams must be a list");
  const teams: OrgTeam[] = [];
  for (const [i, raw] of input.teams.entries()) {
    const team = validateTeam(raw, i);
    if (!team.ok) return bad(team.error);
    if (teams.some((t) => t.id === team.value.id))
      return bad(`duplicate team id "${team.value.id}"`);
    teams.push(team.value);
  }

  if (!Array.isArray(input.glossary)) return bad("glossary must be a list");
  if (input.glossary.length > 200) return bad("glossary holds at most 200 entries");
  const glossary: OrgGlossaryEntry[] = [];
  for (const [i, raw] of input.glossary.entries()) {
    if (!isRecord(raw)) return bad(`glossary[${i}] must be an object`);
    const term = str(raw.term, `glossary[${i}].term`, 80, 1);
    if (!term.ok) return bad(term.error);
    const meaning = str(raw.meaning, `glossary[${i}].meaning`, 300, 1);
    if (!meaning.ok) return bad(meaning.error);
    glossary.push({ term: term.value, meaning: meaning.value });
  }

  const writingStyle = str(input.writingStyle ?? "", "writingStyle", 500);
  if (!writingStyle.ok) return bad(writingStyle.error);
  const brand = validateBrand(input.brand);
  if (!brand.ok) return bad(brand.error);
  const support = str(input.supportContact, "supportContact", 320, 1);
  if (!support.ok) return bad(support.error);
  if (!EMAIL_RE.test(support.value)) return bad("supportContact must be an email address");
  const assistantName = nullableStr(input.assistantName, "assistantName", 40);
  if (!assistantName.ok) return bad(assistantName.error);

  return ok({
    name: name.value,
    legalName: legalName.value,
    description: description.value,
    industry: industry.value,
    emailDomains,
    teams,
    glossary,
    writingStyle: writingStyle.value,
    brand: brand.value,
    supportContact: support.value.toLowerCase(),
    assistantName: assistantName.value,
  });
}

export function validateOrgSwitches(input: unknown): Validated<OrgSwitches> {
  if (!isRecord(input)) return bad("switches must be an object");
  const features = knownIds(input.features ?? [], "features", FEATURE_IDS);
  if (!features.ok) return bad(features.error);
  const integrations = knownIds(input.integrations ?? [], "integrations", INTEGRATION_IDS);
  if (!integrations.ok) return bad(integrations.error);
  const bots = knownIds(input.bots ?? [], "bots", BOT_IDS);
  if (!bots.ok) return bad(bots.error);
  return ok({ features: features.value, integrations: integrations.value, bots: bots.value });
}

/** Settings are what is ON, and may only name what the org is entitled to (spec §6). */
export function validateOrgSettings(
  input: unknown,
  entitlements: OrgSwitches,
): Validated<OrgSwitches> {
  const settings = validateOrgSwitches(input);
  if (!settings.ok) return settings;
  for (const key of ["features", "integrations", "bots"] as const) {
    for (const id of settings.value[key]) {
      if (!entitlements[key].includes(id)) {
        return bad(`settings.${key}: "${id}" is not in this org's entitlements`);
      }
    }
  }
  return settings;
}

export function validateOrgSignin(input: unknown): Validated<OrgSignin> {
  if (!isRecord(input)) return bad("signin must be an object");
  if (input.provider !== "microsoft" && input.provider !== "google") {
    return bad('signin.provider must be "microsoft" or "google"');
  }
  if (input.app !== "shared" && input.app !== "own") {
    return bad('signin.app must be "shared" or "own"');
  }
  const clientId = nullableStr(input.clientId, "signin.clientId", 200);
  if (!clientId.ok) return bad(clientId.error);
  const raw = nullableStr(input.tenantId, "signin.tenantId", 200);
  if (!raw.ok) return bad(raw.error);
  let tenantId = raw.value;
  if (input.provider === "microsoft" && tenantId !== null) {
    tenantId = tenantId.toLowerCase();
    if (!TENANT_ID_RE.test(tenantId)) {
      return bad(
        "signin.tenantId must be the tenant's GUID (the Directory (tenant) ID), not a domain",
      );
    }
  }
  if (input.app === "own" && clientId.value === null) {
    return bad("signin.clientId is required for an org that brings its own app");
  }
  // Exactly four keys: consent and consentError are the callback's to write.
  return ok({
    provider: input.provider,
    app: input.app,
    clientId: clientId.value,
    tenantId,
  });
}

/** Narrow a stored `signin.consent`. Absent or null is `null`. */
export function validateOrgConsent(input: unknown): Validated<OrgConsent | null> {
  if (input === null || input === undefined) return ok(null);
  if (!isRecord(input)) return bad("signin.consent must be an object");
  if (typeof input.tenantId !== "string" || !TENANT_ID_RE.test(input.tenantId)) {
    return bad("signin.consent.tenantId must be a tenant GUID");
  }
  if (!isIsoTime(input.grantedAt)) return bad("signin.consent.grantedAt must be an ISO time");
  return ok({ tenantId: input.tenantId, grantedAt: input.grantedAt });
}

/** Narrow a stored `signin.consentError`. Absent or null is `null`. */
export function validateOrgConsentError(input: unknown): Validated<OrgConsentError | null> {
  if (input === null || input === undefined) return ok(null);
  if (!isRecord(input)) return bad("signin.consentError must be an object");
  if (typeof input.code !== "string" || !CONSENT_CODE_RE.test(input.code)) {
    return bad(`signin.consentError.code must match ${CONSENT_CODE_RE}`);
  }
  if (!isIsoTime(input.at)) return bad("signin.consentError.at must be an ISO time");
  return ok({ code: input.code, at: input.at });
}
